import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api.js';

export default function JoinGame() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(() =>
    (searchParams.get('code') || '').toUpperCase().slice(0, 8)
  );
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const navigate = useNavigate();
  // When we land here from a QR code, auto-join once the code is set.
  const autoJoined = useRef(false);

  const joinWithCode = async (rawCode) => {
    const clean = rawCode.trim().toUpperCase();
    if (!clean) return;
    setBusy(true);
    setError(null);
    try {
      await api.joinGame(clean);
      navigate(`/game/${clean}/claim`);
    } catch (err) {
      setError('No game found with that code');
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    joinWithCode(code);
  };

  // Auto-join when the screen loads with a ?code= query param (QR scan).
  useEffect(() => {
    if (autoJoined.current) return;
    const qp = (searchParams.get('code') || '').trim();
    if (qp.length >= 4) {
      autoJoined.current = true;
      joinWithCode(qp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the scanner returns a value, extract the session code from the
  // URL (pubgolfgame.com/join?code=XXXX) or treat it as a raw code.
  const handleScanResult = (value) => {
    setScanOpen(false);
    if (!value) return;
    let extracted = value;
    try {
      const u = new URL(value);
      const q = u.searchParams.get('code');
      if (q) extracted = q;
    } catch {
      // not a URL — use as-is
    }
    const clean = extracted.trim().toUpperCase().slice(0, 8);
    setCode(clean);
    joinWithCode(clean);
  };

  return (
    <form className="screen" onSubmit={submit}>
      <div className="screen-header">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>Join Game</h1>
        <div className="spacer" />
      </div>

      <div className="text-center text-muted" style={{ marginBottom: '2rem' }}>
        Enter the 5-character code from your host
      </div>

      <input
        className="code-input"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
        placeholder="ABCDE"
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
      />
      {error && (
        <div className="text-center" style={{ color: 'var(--danger)', marginTop: '1rem' }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary btn-block btn-lg"
        style={{ marginTop: '1.5rem' }}
        disabled={busy || code.length < 4}
      >
        {busy ? 'Joining…' : 'Join'}
      </button>

      <div className="join-or">
        <span />
        <em>or</em>
        <span />
      </div>

      <button
        type="button"
        className="btn btn-ghost btn-block btn-lg"
        onClick={() => setScanOpen(true)}
      >
        📷 Scan QR Code
      </button>

      {scanOpen && (
        <QrScanner
          onClose={() => setScanOpen(false)}
          onResult={handleScanResult}
        />
      )}
    </form>
  );
}

// ===== Lightweight QR scanner =====
// Tries the native BarcodeDetector API first (supported in Chrome on
// Android/desktop). Falls back to prompting the user to use their
// phone's camera app if unavailable — we intentionally don't bundle a
// heavy QR library to keep the initial download small.
function QrScanner({ onClose, onResult }) {
  const videoRef = useRef(null);
  const [err, setErr] = useState(null);
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let stream = null;
    let rafId = null;
    let detector = null;

    (async () => {
      if (typeof window === 'undefined') return;
      if (!('BarcodeDetector' in window)) {
        setUnsupported(true);
        return;
      }
      try {
        // eslint-disable-next-line no-undef
        detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      } catch {
        setUnsupported(true);
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch (e) {
        setErr(e.message || 'Camera access denied');
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      const tick = async () => {
        if (cancelled || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes[0]?.rawValue) {
            onResult(codes[0].rawValue);
            return;
          }
        } catch {}
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    })();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [onResult]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet qr-scanner-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="penalty-modal-header">
          <h2>📷 Scan QR Code</h2>
          <span className="text-muted">
            Point at the host's invite code
          </span>
        </div>
        {unsupported ? (
          <div className="route-fallback">
            <p className="text-muted" style={{ marginBottom: '1rem' }}>
              Your browser doesn't support QR scanning. Use your phone's
              camera app to scan the code, then tap the link — or type
              the code above.
            </p>
            <button className="btn btn-primary btn-block" onClick={onClose}>
              Got it
            </button>
          </div>
        ) : err ? (
          <div className="route-fallback">
            <p className="text-muted" style={{ marginBottom: '1rem' }}>{err}</p>
            <button className="btn btn-primary btn-block" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <div className="qr-scanner-video-wrap">
            <video ref={videoRef} playsInline muted />
            <div className="qr-scanner-frame" />
          </div>
        )}
      </div>
    </div>
  );
}
